import React from "react";
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaBullseye, FaColumns, FaMagic, FaCommentDots, FaMobileAlt } from 'react-icons/fa';
import { HiCheckCircle } from 'react-icons/hi';
import { useApp } from '../../../context/AppContext';
import { openAppStudio } from '../../../utils/appStudioUrl';
import "./SalesPageNew.css";

// Public marketing price only. The monthly plan figure is not a headline.
const PRICING = {
   GBP: { symbol: "£", annually: "25" },
   USD: { symbol: "$", annually: "25" },
   EUR: { symbol: "€", annually: "25" },
   SEK: { symbol: " kr", annually: "333", suffix: true },
   AUD: { symbol: "AU$", annually: "46" },
   CAD: { symbol: "CA$", annually: "46" },
   NZD: { symbol: "NZ$", annually: "54" },
};


const hubItemKeys = [
   { emoji: "🏪", key: "customStorefront" },
   { emoji: "♾️", key: "unlimitedProducts" },
   { emoji: "📱", key: "customerApps" },
   { emoji: "🎓", key: "unlimitedCourses" },
   { emoji: "📦", key: "unlimitedDigitalProducts" },
   { emoji: "♻️", key: "unlimitedSubscriptions" },
   { emoji: "🤝", key: "unlimitedCoaching" },
   { emoji: "👥", key: "unlimitedCommunities" },
   { emoji: "💬", key: "messaging" },
   { emoji: "📈", key: "insights" },
   { emoji: "💸", key: "instantPayouts" },
   { emoji: "😎", key: "customerAdmin" },
   { emoji: "🎟️", key: "promoCodes" },
   { emoji: "💌", key: "unlimitedEmails" },
   { emoji: "💳", key: "bnpl" },
   { emoji: "🌐", key: "customDomain" },
   { emoji: "✨", key: "customBranding" },
   { emoji: "💰", key: "affiliates" },
   { emoji: "🖥️", key: "landingPages" },
   { emoji: "🎯", key: "metaPixel" },
   { emoji: "🔗", key: "webhooks" },
   { emoji: "🧩", key: "apiAccess" },
   { emoji: "🤖", key: "mcp" },
   { emoji: "🙋", key: "realHumanSupport" },
   { emoji: "🏷️", key: "zeroCommission" },
   { emoji: "📩", key: "autoDm" },
];

const agentGroupDefs = [
   {
      bucketKey: "findClients",
      jobs: [
         { Icon: FaBullseye, key: "runMetaAds" },
         { Icon: FaColumns, key: "buildLandingPages" },
      ],
   },
   {
      bucketKey: "convertVisitors",
      jobs: [
         { Icon: FaMagic, key: "createContent" },
         { Icon: FaCommentDots, key: "findConvertVisitors" },
      ],
   },
];

export default function HubVsAgents({ windowWidth, logged_in, pre_influencer, influencer_bool, current_user, utmSource, utmMedium, utmCampaign, utmTerm, utmContent, referralId, passiveReferralId }) {
   const { t } = useTranslation();
   const { currency } = useApp();

   const price = PRICING[currency] || PRICING.USD;
   const amount = price.annually;
   const isMobile = windowWidth < 768;
   const isSmall = windowWidth < 640;
   const isLarge = windowWidth >= 1024;

   const buildRegisterUrl = () => {
      let url = '/become-a-creator/register?source=new';
      const id = referralId || passiveReferralId;
      if (id) url += '&id=' + id;
      const utms = { utm_source: utmSource, utm_medium: utmMedium, utm_campaign: utmCampaign, utm_content: utmContent, utm_term: utmTerm };
      Object.entries(utms).forEach(([k, v]) => { if (v) url += '&' + k + '=' + v; });
      return url;
   };

   const handleCTA = () => {
      if (logged_in === true) {
         if (pre_influencer === true && influencer_bool !== true) {
            window.location.assign(buildRegisterUrl());
         } else if (pre_influencer === false && influencer_bool !== true) {
            window.location.assign(buildRegisterUrl());
         } else {
            window.location.assign('/' + current_user);
         }
      } else {
         window.location.assign(buildRegisterUrl());
      }
   };

   return (
      <section id="how" style={{ maxWidth: 1152, margin: '0 auto', padding: '80px 24px' }}>
         <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 24,
            alignItems: 'start',
            alignContent: 'start',
         }}>
            {/* HUB CARD */}
            <div id="pricing" className="sp-card-lift" style={{
               borderRadius: 24, border: '1px solid var(--sp-bg-soft-2)', background: '#fff',
               padding: isMobile ? 24 : 32, scrollMarginTop: 96,
            }}>
               <h3 style={{ fontSize: isMobile ? 30 : 36, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                  {t('salesPageNew.hub.title')}
               </h3>
               <p style={{ marginTop: 12, color: 'var(--sp-text-soft)', fontSize: 16, lineHeight: 1.7, textTransform: 'none', fontWeight: 400 }}>
                  {t('salesPageNew.hub.description')}
               </p>

               {/* Price */}
               <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
                  <span style={{
                     fontSize: isSmall ? 36 : (isMobile ? 48 : (isLarge ? 72 : 48)),
                     fontWeight: 800, letterSpacing: '-0.02em', lineHeight: isLarge ? 1 : 1.1,
                     color: 'var(--sp-ink)',
                  }}>
                     {price.suffix ? `${amount}${price.symbol}` : `${price.symbol}${amount}`}
                  </span>
                  <span style={{ fontSize: isSmall ? 12 : (isMobile ? 14 : (isLarge ? 18 : 13)), color: 'var(--sp-text-soft)' }}>
                     {t('salesPageNew.hub.billedAnnually')}
                  </span>
               </div>

               {/* Features list */}
               <ul style={{
                  marginTop: 32, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '16px 24px', listStyle: 'none', padding: 0,
               }}>
                  {hubItemKeys.map(it => (
                     <li key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 18, lineHeight: '24px' }}>{it.emoji}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-ink)', textTransform: 'none' }}>{t(`salesPageNew.hub.features.${it.key}`)}</span>
                     </li>
                  ))}
               </ul>

               <div style={{
                     marginTop: 32, borderRadius: 16,
                     border: '1px solid var(--sp-border, #E4EBF6)',
                     background: 'var(--sp-bg-soft, #F8FAFC)',
                     padding: isMobile ? 20 : 24,
                  }}>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, paddingBottom: 12, borderBottom: '1px solid var(--sp-border, #E4EBF6)' }}>
                        <span style={{ fontSize: 14, color: 'var(--sp-text-soft)' }}>{t('salesPageNew.hub.value.sixMonths', '6 months of WithMe, free')}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.included', 'included')}</span>
                     </div>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: '1px solid var(--sp-border, #E4EBF6)' }}>
                        <span style={{ fontSize: 14, color: 'var(--sp-text-soft)' }}>{t('salesPageNew.hub.value.groupCoaching', 'Weekly Group Coaching Sessions')}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.groupCoachingWorth', '$627')}</span>
                     </div>
                     <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, paddingTop: 16 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--sp-ink)' }}>{t('salesPageNew.hub.value.extra', 'Extra value with annual')}</span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--sp-brand)', whiteSpace: 'nowrap' }}>{t('salesPageNew.hub.value.extraAmount', '$627 + 6 months')}</span>
                     </div>
                  </div>

               {/* CTA */}
               <button
                  onClick={handleCTA}
                  style={{
                     marginTop: 32, display: 'block', width: '100%', borderRadius: 12,
                     background: 'var(--sp-brand)', padding: '16px 24px', textAlign: 'center',
                     fontSize: 16, fontWeight: 600, color: '#fff', border: 'none', cursor: 'pointer',
                     transition: 'background 0.2s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--sp-brand-dark)'}
                  onMouseOut={e => e.currentTarget.style.background = 'var(--sp-brand)'}
               >
                  {t('salesPageNew.hub.startTrial')}
               </button>
               <p style={{ marginTop: 12, textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--sp-text-soft)' }}>
                  {t('salesPageNew.hub.value.ctaNote', 'Includes Weekly Group Coaching Sessions, worth $627')}
               </p>

            </div>

            {/* RIGHT COLUMN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, position: isMobile ? 'relative' : 'sticky', top: isMobile ? 'auto' : 96 }}>
               {/* AGENTS CARD */}
               <div className="sp-card-lift" style={{
                  borderRadius: 24, border: '1px solid #FFD8B8', padding: 32,
                  background: 'linear-gradient(180deg, #FFF6EE, #FFFFFF)',
               }}>
                  <p className="sp-eyebrow">{t('salesPageNew.agents.eyebrow')}</p>
                  <h3 style={{ marginTop: 8, fontSize: isMobile ? 48 : 60, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                     {t('salesPageNew.agents.title')}{" "}
                     <span className="sp-accent-italic-agent">{t('salesPageNew.agents.titleAccent')}</span>
                  </h3>
                  <p style={{ marginTop: 12, color: 'var(--sp-text-soft)', fontSize: 16, lineHeight: 1.7, textTransform: 'none', fontWeight: 400 }}>
                     {t('salesPageNew.agents.description')}
                  </p>

                  <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
                     {agentGroupDefs.map(group => (
                        <div key={group.bucketKey}>
                           <p className="sp-eyebrow">{t(`salesPageNew.agents.${group.bucketKey}`)}</p>
                           <ul style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
                              {group.jobs.map(it => (
                                 <li key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--sp-ink)' }}>
                                    <span style={{
                                       display: 'flex', alignItems: 'center', justifyContent: 'center',
                                       width: 36, height: 36, borderRadius: 12, background: '#fff',
                                       color: 'var(--sp-agent-ad)', boxShadow: 'inset 0 0 0 1px #FFD8B8',
                                    }}>
                                       <it.Icon size={18} />
                                    </span>
                                    <span style={{ fontSize: 14, fontWeight: 500, textTransform: 'none', lineHeight: 1.4 }}>{t(`salesPageNew.agents.${it.key}`)}</span>
                                 </li>
                              ))}
                           </ul>
                        </div>
                     ))}
                  </div>
               </div>

               {/* CUSTOM APP CARD */}
               <div className="sp-card-lift" style={{
                  borderRadius: 24, border: '1px solid #C8B8FF', padding: '20px 24px',
                  background: 'linear-gradient(180deg, #F3EEFF, #FFFFFF)',
               }}>
                  <p className="sp-eyebrow" style={{ color: '#7C3AED', fontSize: 10 }}>{t('salesPageNew.customApp.eyebrow')}</p>
                  <h3 style={{ marginTop: 4, fontSize: isMobile ? 24 : 28, letterSpacing: '-0.02em', color: 'var(--sp-ink)' }}>
                     {t('salesPageNew.customApp.title')}{" "}
                     <span style={{ fontStyle: 'italic', color: '#7C3AED' }}>{t('salesPageNew.customApp.titleAccent')}</span>
                  </h3>
                  <p style={{ marginTop: 8, color: 'var(--sp-text-soft)', fontSize: 14, lineHeight: 1.6, textTransform: 'none', fontWeight: 400 }}>
                     {t('salesPageNew.customApp.description')}
                  </p>

                  <button
                     onClick={() => openAppStudio(current_user, 'sales_page')}
                     style={{
                        marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', borderRadius: 12, background: '#7C3AED', padding: '12px 20px',
                        textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#fff',
                        border: 'none', cursor: 'pointer', transition: 'background 0.2s',
                     }}
                     onMouseOver={e => e.currentTarget.style.background = '#6D28D9'}
                     onMouseOut={e => e.currentTarget.style.background = '#7C3AED'}
                  >
                     <FaMobileAlt size={16} />
                     {t('salesPageNew.customApp.cta')}
                  </button>
               </div>
            </div>
         </div>
      </section>
   );
}
